import React, { useEffect, useMemo, useState } from 'react'
import {
  makeStyles,
  createStyles,
  Typography,
  Grid,
  Fade,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core'
import clsx from 'clsx'
import { BillingAddress, ShippingAddress } from '@/typings/base'
import TextFieldDefault from '../TextField/TextField'
import Button from '../Button/Button'
import { connect } from 'react-redux'
import Loader from '../Loader/Loader'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import CMSText, { isPrismicText, prismicText } from '../Global/CMSText'
import { useAddress } from '@/hooks/useAddress'
import AddressForm from './AddressForm'
import { PrismicGeneralData } from '@/interfaces/common/prismicGeneralConfig'
import { isB2B, isShippingCompanyEnabled } from '../../utils/constants'
import { PrismicFunnelData } from '@/interfaces/common/prismicFunnel'

export type TFields = {
  required?: boolean
  placeholder?: string
  label?: string
  message?: string
}

export type TInformationFormProps = {
  className?: string
  classes?: object
  switchForm: any
  user: any
  t: any
  text: any
  accountText: any
  textFieldComponent?: (props: any) => JSX.Element
  onSubmit: (
    shippingAddress: ShippingAddress,
    billingAddress?: BillingAddress
  ) => void
  information?: BillingAddress
  // saveUser: (email: string) => void
  infoMessage?: string
  error?: { field: string; message: string }
  hasError: boolean
  errorPhone: boolean
  disabled?: boolean
  cart: any
  prefill?: boolean
  generalConfigData?: PrismicGeneralData
  prismicFunnelData: PrismicFunnelData
}

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      margin: theme.spacing(0, 0, 2),
    },
    form: {},
    formContent: {
      margin: theme.spacing(0, 0, 2),
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(0, 2),
      },
    },
    label: {},
    labelCheckbox: {
      display: 'flex',
      cursor: 'pointer',
      marginTop: 10,
      marginBottom: 20,
      [theme.breakpoints.up('md')]: {
        alignItems: 'center',
      },
    },
    checkboxInput: {
      border: '1px solid #efefef',
      background: '#fff',
      marginBottom: 0,
      marginRight: 5,
      lineHeight: 'normal',
      width: 15,
      height: 15,
    },
    labelCheckboxSpan: {
      color: theme.typography.body1.color,
      fontSize: '11px',
      lineHeight: '17px',
      display: 'inline-block',
      cursor: 'pointer',
      fontWeight: 'normal',
      marginBottom: '0px',
    },
    couponCodeTxt: {
      marginTop: '8px',
      marginBottom: '10px',
      cursor: 'pointer',
      textDecoration: 'underline',
      fontSize: '12px',
    },
    messageContainer: {
      marginTop: '8px',
      marginBottom: '10px',
      fontSize: '12px',
    },
    errorMessage: {
      color: 'red',
    },
    backLink: {
      color: theme.extraColors.greyText,
      marginBottom: theme.spacing(2),
      display: 'inline-flex',
    },
    backArrow: {
      width: 20,
      marginRight: theme.spacing(1),
    },
    formSubmit: {
      textAlign: 'center',
      '&> .MuiButton-root': {
        width: '100%',
        [theme.breakpoints.up('sm')]: {
          width: '50%',
        },
      },
    },
    createAccount: {
      marginTop: theme.spacing(2),
      textAlign: 'center',
      textDecoration: 'underline',
      cursor: 'pointer',
      '&> a': {
        color: 'inherit',
      },
    },
    hide: {
      opacity: 0,
      transition: theme.transitions.create('opacity'),
    },
    show: {
      opacity: 1,
      transition: theme.transitions.create('opacity'),
    },
    shippingAddressNoBottomMargin: {
      marginBottom: 0,
    },
    sameAddressCheckbox: {
      marginBottom: theme.spacing(2),
    },
  })
)

const InformationForm = (props: TInformationFormProps) => {
  const classes = useStyles(props)
  const {
    textFieldComponent: TextField,
    onSubmit,
    text,
    accountText,
    t,
    user,
    switchForm,
    infoMessage,
    error,
    cart,
    hasError,
    errorPhone,
    disabled = false,
    prefill = false,
    generalConfigData,
    information,
  } = props
  const { trackEvent } = useMatomo()
  const {
    address: shippingAddress,
    resetInputAddress: resetInputShippingAddress,
  } = useAddress('shipping')
  const {
    address: billingAddress,
    resetInputAddress: resetInputBillingAdress,
  } = useAddress('billing')

  const [
    billingAndShippingAddressesAreTheSame,
    setBillingAndShippingAddressesAreTheSame,
  ] = useState(true)

  const [userInfo, setUserInfo] = useState({
    firstName: {
      type: 'text',
      value: information?.firstName || user?.user?.firstName || '',
    },
    lastName: {
      type: 'text',
      value: information?.lastName || user?.user?.lastName || '',
    },
    email: {
      type: 'email',
      value:
        (typeof window !== 'undefined' && localStorage?.email) ||
        information?.email ||
        user?.user?.email ||
        '',
    },
    phone: {
      type: 'tel',
      value: information?.phoneNumber || user?.user?.phoneNumber || '',
    },
    vatNumber: {
      type: 'text',
      value: information?.vatNumber || user?.user?.vatNumber || '',
    },
    company: {
      // Fallback sur la raison sociale du compte (shopName B2B SSO, ou company de
      // l'adresse) si le panier ne la porte pas encore.
      type: 'text',
      value:
        information?.company ||
        user?.user?.shopName ||
        user?.user?.address?.company ||
        '',
    },
  })

  const disableContinueButton = useMemo(() => {
    const userInfosFormValid =
      userInfo.firstName.value &&
      userInfo.lastName.value &&
      userInfo.email.value &&
      userInfo.phone.value
    const B2BFormValid = userInfo.company.value && userInfo.vatNumber.value

    return (
      !userInfosFormValid ||
      !shippingAddress.isValid ||
      (isB2B && !B2BFormValid) ||
      (!billingAndShippingAddressesAreTheSame && !billingAddress.isValid)
    )
  }, [
    userInfo,
    shippingAddress.isValid,
    billingAndShippingAddressesAreTheSame,
    billingAddress.isValid,
    isB2B,
  ])

  const bottomText = accountText?.information_bottom_text

  const usedFields = useMemo(() => {
    if (isB2B) {
      return Object.keys(userInfo)
    }
    // Hors B2B : pas de vatNumber, et company seulement si activé (raison sociale
    // livraison B2B Atlantic/Thermor), positionné juste après le nom.
    const fields = Object.keys(userInfo).filter(
      (field) => field !== 'vatNumber' && field !== 'company'
    )
    if (isShippingCompanyEnabled) {
      fields.splice(fields.indexOf('lastName') + 1, 0, 'company')
    }
    return fields
  }, [userInfo, isB2B])

  const handleInputChange = (field: string) => (value: string) => {
    setUserInfo((prev) => ({
      ...prev,
      [field]: { ...prev[field], value },
    }))
  }

  const handleCheckboxChange = (event) => {
    setBillingAndShippingAddressesAreTheSame(event.target.checked)
  }

  const onSubmitForm = (e) => {
    e.preventDefault()
    localStorage.setItem('email', userInfo.email.value)

    const userProps = {
      firstName: userInfo.firstName.value.trim(),
      lastName: userInfo.lastName.value.trim(),
      phoneNumber: userInfo.phone.value,
      company: userInfo.company.value.trim(),
    }

    const billingAddressPayload = billingAndShippingAddressesAreTheSame
      ? shippingAddress
      : billingAddress

    onSubmit(
      {
        ...userProps,
        ...shippingAddress.payload,
      },
      {
        ...userProps,
        vatNumber: userInfo.vatNumber.value.trim(),
        ...billingAddressPayload.payload,
      }
    )
    resetInputShippingAddress()
    resetInputBillingAdress()
  }

  useEffect(() => {
    const isShippingSameAsBilling = cart.item?.isShippingSameAsBilling
    if (isShippingSameAsBilling !== undefined) {
      setBillingAndShippingAddressesAreTheSame(isShippingSameAsBilling)
    }
  }, [cart.item?.isShippingSameAsBilling])

  // Pré-remplit les champs connus depuis le compte utilisateur s'il se charge
  // après le montage, uniquement pour les champs encore vides (sans écraser une
  // saisie en cours).
  useEffect(() => {
    const accountValues: Record<string, string | undefined> = {
      firstName: user?.user?.firstName,
      lastName: user?.user?.lastName,
      email: user?.user?.email,
      phone: user?.user?.phoneNumber,
      vatNumber: user?.user?.vatNumber,
      company: user?.user?.shopName || user?.user?.address?.company,
    }
    setUserInfo((prev) => {
      let changed = false
      const next = { ...prev }
      Object.entries(accountValues).forEach(([key, value]) => {
        if (value && !prev[key]?.value) {
          next[key] = { ...prev[key], value }
          changed = true
        }
      })
      return changed ? next : prev
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user])

  return (
    <Fade in>
      <div className={clsx(classes.root)}>
        <form className={clsx(classes.form)} onSubmit={onSubmitForm}>
          <div className={clsx(classes.formContent)}>
            <Grid container spacing={2}>
              {usedFields.map((key) => (
                <Grid item xs={key === 'company' ? 12 : 6} key={key}>
                  <TextField
                    label={prismicText(
                      text?.[key],
                      t(`texts:account:${key.toLowerCase()}`)
                    )}
                    name={key}
                    placeholder={prismicText(
                      text?.[key],
                      t(`texts:account:${key.toLowerCase()}`)
                    )}
                    value={userInfo[key].value}
                    onChange={handleInputChange(key)}
                    required={key !== 'company'}
                    phoneInputInfosIcon={
                      key === 'tel'
                        ? {
                            tooltipTitle: generalConfigData?.phone_input_infos,
                          }
                        : undefined
                    }
                  />
                </Grid>
              ))}
            </Grid>
            <AddressForm
              className={clsx({
                [classes.shippingAddressNoBottomMargin]: isB2B,
              })}
              prefill={prefill}
              addressType="shipping"
              defaultAddress={user?.user?.address}
              hideAdditionalAddress={
                props.prismicFunnelData?.hide_additional_address
              }
            />
            {(isB2B ||
              process.env.IS_SHIPPING_ADDRESS_SEPARATE === 'true' ||
              generalConfigData?.is_shipping_address_separate) && (
              <FormControlLabel
                className={classes.sameAddressCheckbox}
                control={
                  <Checkbox
                    checked={billingAndShippingAddressesAreTheSame}
                    onChange={handleCheckboxChange}
                  />
                }
                label={t('texts:general:search_address:same_address')}
              />
            )}
            {!billingAndShippingAddressesAreTheSame && (
              <AddressForm
                prefill={prefill}
                addressType="billing"
                defaultAddress={user?.user?.address}
                hideAdditionalAddress={
                  props.prismicFunnelData?.hide_additional_address
                }
              />
            )}
            {infoMessage && (
              <div className={clsx(classes.messageContainer)}>
                <p>{infoMessage}</p>
              </div>
            )}
            {error && error.message && (
              <div
                className={clsx(classes.messageContainer, classes.errorMessage)}
              >
                <p>{error.message}</p>
              </div>
            )}
            {hasError && (
              <Typography variant={'body2'} color={'error'} align={'center'}>
                <CMSText
                  asText
                  data={text?.error_happend}
                  defaultText={t('texts:account:error_happend')}
                />
              </Typography>
            )}
            {errorPhone && (
              <Typography variant={'body2'} color={'error'} align={'center'}>
                <CMSText
                  asText
                  data={text?.error_phone}
                  defaultText={t('texts:account:error_phone')}
                />
              </Typography>
            )}
          </div>
          {isPrismicText(bottomText) && (
            <Typography
              variant={'body2'}
              component={'div'}
              className={'account__bottom-text'}
            >
              <CMSText data={bottomText} />
            </Typography>
          )}
          <div className={classes.formSubmit}>
            <Button
              size="large"
              type="submit"
              data-testid={`nextInformation`}
              color="secondary"
              onClick={() =>
                trackEvent({
                  category: 'User Information',
                  action: `Valid Information form`,
                })
              }
              variant="contained"
              className="offer-subscribe"
              disabled={
                cart.loading ||
                userInfo.firstName.value === '' ||
                userInfo.lastName.value === '' ||
                userInfo.email.value === '' ||
                disableContinueButton ||
                disabled
              }
            >
              <span className={cart.loading ? classes.hide : classes.show}>
                <CMSText
                  asText
                  data={text?.next1}
                  defaultText={t('texts:account:next')}
                />
              </span>
              {cart.loading && <Loader />}
            </Button>
          </div>
          {!user?.user?.id && (
            <div className={classes.createAccount}>
              <Typography
                variant={'body2'}
                color={'textSecondary'}
                onClick={() => switchForm(1)}
              >
                <CMSText
                  asText
                  data={text?.create_account_link}
                  defaultText={t('texts:account:login_link')}
                />
              </Typography>
            </div>
          )}
        </form>
      </div>
    </Fade>
  )
}

InformationForm.defaultProps = {
  textFieldComponent: TextFieldDefault,
}

const mapStateToProps = (state) => ({
  cart: state.cart,
  user: state.user,
})

export default connect(mapStateToProps)(InformationForm)
